import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";

import { PackageOfferHint } from "/imports/ui/components/booking";
import { formatPrice } from "../classHelpers";
import { WaitlistButton, LeaveWaitlistLink, waitlistState, waitlistCaption } from "./WaitlistAction";

const LABEL_CHIP_SX = { fontSize: { xs: "0.6rem", md: "0.65rem" }, height: { xs: "1.125rem", md: "1.25rem" } };

// Full horizontal class row — used in the Day-view day sections. The card body
// opens the session detail page (info + who's playing); the Join button keeps
// its one-tap path straight to checkout.
function ClassCard({ cls, currency, onJoin, onOpen, packageOffer, onViewPackage, onJoinWaitlist, onLeaveWaitlist }) {
  const isFull = cls.spotsAvailable === 0;
  const { eligible, reason, needsAssessment } = cls.eligibility || {};
  const canJoin = !isFull && eligible !== false && !cls.isJoined;
  // A full session with a waitlist is not a dead end, so it keeps full contrast.
  const waitlist = waitlistState(cls);

  // A spot open for a listed client means two buttons, [Leave waitlist] [Join] —
  // more than a phone's price column can hold without crushing the class
  // details, so there they drop to their own row. A single button stays where
  // Join always sits.
  const wideActions = waitlist.spotOpen;

  const levelChip = cls.levelRequiredNames ? (
    <Chip label={cls.levelRequiredNames} size="small" variant="outlined" color="warning" sx={LABEL_CHIP_SX} />
  ) : null;

  // Waitlist cards carry an extra chip and wider buttons: give them room.
  const pad = waitlist.listed || waitlist.show ? { xs: 2, md: 2.25 } : { xs: 1.5, md: 2 };

  // The client's standing on this session's waitlist — "Spot open" once a seat
  // frees and it's theirs to book. Phone: above the buttons; desktop: beside the level.
  const waitlistChip = waitlist.listed ? (
    <Chip
      label={waitlist.spotOpen ? "Spot open" : "On waitlist"}
      size="small"
      variant={waitlist.spotOpen ? "filled" : "outlined"}
      color="primary"
      sx={{ ...LABEL_CHIP_SX, flexShrink: 0, ...(waitlist.spotOpen && { color: "common.white", fontWeight: 700 }) }}
    />
  ) : null;

  // Once a seat opens for a listed client: [Leave waitlist] [Join].
  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <LeaveWaitlistLink
        cls={cls}
        onLeave={onLeaveWaitlist}
        stopPropagation
        sx={{
          px: { xs: 1.25, md: 2 },
          py: { xs: "calc(0.2rem - 1px)", md: "calc(0.25rem - 1px)" },
          fontSize: { xs: "0.75rem", md: "0.8125rem" }
        }}
      />
      {waitlist.show ? (
        <WaitlistButton
          cls={cls}
          onJoin={onJoinWaitlist}
          onLeave={onLeaveWaitlist}
          stopPropagation
          icons={false}
          sx={{
            px: { xs: 1.25, md: 2 },
            py: { xs: "calc(0.2rem - 1px)", md: "calc(0.25rem - 1px)" },
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            whiteSpace: "nowrap"
          }}
        />
      ) : (
        <Button
          size="small"
          variant={cls.isJoined ? "outlined" : canJoin ? "contained" : "outlined"}
          disabled={!canJoin}
          onClick={(e) => {
            e.stopPropagation();
            onJoin();
          }}
          sx={{
            minWidth: { xs: "3.5rem", md: "4rem" },
            px: { xs: 1.25, md: 2 },
            py: { xs: 0.4, md: 0.5 },
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            ...(cls.isJoined && { borderColor: "success.main", color: "success.main" })
          }}
        >
          {cls.isJoined ? "Joined" : isFull ? "Full" : needsAssessment ? "Assess" : cls.wasJoined ? "Re-join" : "Join"}
        </Button>
      )}
    </Box>
  );

  return (
    <Paper
      elevation={0}
      onClick={onOpen}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        borderRadius: { xs: 3, md: 4 },
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 4px 20px rgba(26,26,26,0.04)",
        overflow: "hidden",
        opacity: isFull && !waitlist.show ? 0.65 : 1,
        ...(onOpen && { cursor: "pointer", "&:hover": { borderColor: "primary.main" } })
      }}
    >
      <Box
        sx={{
          minWidth: { xs: "4.5rem", md: "5.5rem" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 1.25, md: 2 },
          px: { xs: 1, md: 1.5 },
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          borderRight: "1px solid",
          borderColor: "divider",
          flexShrink: 0
        }}
      >
        {cls.date ? (
          <Typography
            color="primary.main"
            fontWeight={700}
            sx={{ mb: 0.75, fontSize: { xs: "0.68rem", md: "0.75rem" }, letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap" }}
          >
            {DateTime.fromISO(cls.date).toFormat("ccc d LLL")}
          </Typography>
        ) : null}
        <Typography fontWeight={700} lineHeight={1} sx={{ fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
          {cls.time}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: "0.68rem", md: "0.75rem" } }}>
          {cls.duration} min
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", flex: 1, minWidth: 0 }}>
          <Box sx={{ flex: 1, p: pad, minWidth: 0 }}>
            <Typography fontWeight={700} noWrap sx={{ fontSize: { xs: "0.9rem", md: "1rem" } }}>
              {cls.name}
            </Typography>
            {cls.trainer && (
              <Typography color="text.secondary" sx={{ fontSize: { xs: "0.78rem", md: "0.875rem" } }}>
                with {cls.trainer.name}
              </Typography>
            )}
            {(cls.facility || cls.location) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, minWidth: 0 }}>
                <PlaceOutlinedIcon sx={{ fontSize: { xs: "0.95rem", md: "1.05rem" }, color: "primary.main", flexShrink: 0 }} />
                <Typography noWrap color="text.secondary" sx={{ fontSize: { xs: "0.72rem", md: "0.8125rem" } }}>
                  {cls.facility?.name}
                  {cls.facility && cls.location ? " · " : ""}
                  {cls.location?.name ? (
                    <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontSize: { xs: "0.875rem", md: "0.9375rem" } }}>
                      {cls.location.name}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            )}
            {levelChip || waitlistChip ? (
              <Box sx={{ mt: 0.5, gap: 0.5, flexWrap: "wrap", display: { xs: levelChip && !wideActions ? "flex" : "none", md: "flex" } }}>
                {levelChip}
                {waitlistChip ? <Box sx={{ display: { xs: "none", md: "flex" } }}>{waitlistChip}</Box> : null}
              </Box>
            ) : null}
            {!canJoin && reason ? (
              <Typography color="warning.main" sx={{ display: "block", mt: 0.5, fontSize: { xs: "0.68rem", md: "0.75rem" } }}>
                {reason}
              </Typography>
            ) : null}
            {/* Whether an offer belongs on this class at all is settled in
            useClassBooking.packageOfferFor — the popover and the session page
            answer it the same way. On a phone it is the card's footer instead. */}
            {packageOffer ? (
              <Box sx={{ mt: 0.5, display: { xs: "none", md: "block" } }}>
                <PackageOfferHint offer={packageOffer} currency={currency} onClick={() => onViewPackage(packageOffer, cls)} />
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: { xs: "flex-start", md: "center" },
              gap: { xs: 0.5, md: 0.75 },
              p: pad,
              flexShrink: 0
            }}
          >
            {cls.price != null && (
              <Typography fontWeight={700} sx={{ fontSize: { xs: "0.85rem", md: "0.875rem" } }}>
                {formatPrice(cls.price, currency)}
              </Typography>
            )}
            <Typography
              color={cls.isJoined ? "success.main" : isFull ? "error.main" : "text.secondary"}
              sx={{ fontSize: { xs: "0.65rem", md: "0.75rem" }, whiteSpace: "nowrap" }}
            >
              {cls.isJoined
                ? "You're in"
                : (!waitlist.listed && waitlistCaption(cls)) ||
                  (isFull ? "Full" : `${cls.spotsAvailable} spot${cls.spotsAvailable !== 1 ? "s" : ""} left`)}
            </Typography>
            {waitlistChip ? <Box sx={{ display: { xs: "flex", md: "none" } }}>{waitlistChip}</Box> : null}
            <Box sx={{ display: wideActions ? { xs: "none", md: "flex" } : "flex" }}>{actions}</Box>
          </Box>
        </Box>
        {wideActions ? (
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", justifyContent: "space-between", gap: 1, px: pad, pb: pad }}>
            <Box>{levelChip}</Box>
            {actions}
          </Box>
        ) : null}
      </Box>

      {/* Phone: the details column is too narrow for a readable offer, so it
          runs the full width of the card on a line of its own. */}
      {packageOffer ? (
        <Box sx={{ display: { xs: "block", md: "none" }, flexBasis: "100%" }}>
          <PackageOfferHint
            offer={packageOffer}
            currency={currency}
            onClick={() => onViewPackage(packageOffer, cls)}
            compact
            sx={{ borderRadius: 0 }}
          />
        </Box>
      ) : null}
    </Paper>
  );
}

export default ClassCard;
